import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {Router} from "@angular/router";
import {LocalStorageService} from "../localstorage/localstorage.service";
import {Constants} from "../../constants";
import {environment} from "../../environments/environment";
import {Parent} from "../../models/parent";
import {StripeInvoice} from "../../models/stripe-invoice";

export interface SiblingDiscount {
    seasonId: string;
    eligible: boolean;
    requestedAt: string | null;
    sibling: {
        athleteId: string;
        firstName: string;
        lastName: string;
        status: string;
        requestedAt: string;
    } | null;
}

@Injectable({
    providedIn: 'root'
})
export class PaymentService {

    constructor(private http: HttpClient, private router: Router, public localStorageService: LocalStorageService, @Inject(PLATFORM_ID) private platformId: any) {
    }

    getRequestHeaders() {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Access-Control-Allow-Origin': '*',
            'Authorization': localStorage.getItem(Constants.LOCAL_TOKEN) ?? environment.publicApiToken
        });
    }

    getPublicRequestHeaders() {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': environment.publicApiToken,
        });
    }

    /** Sconto fratelli calcolato dal backend in base alla data della richiesta di iscrizione per la stagione. */
    async getSiblingDiscount(athleteId: string, seasonId: string): Promise<SiblingDiscount | null> {
        try {
            const response: any = await this.http.get(environment.apiUrl + `/athletePaymentRequests/siblingDiscount/${athleteId}`, {
                params: {seasonId},
                headers: this.getRequestHeaders(),
            }).toPromise();
            return response?.success ? response.data : null;
        } catch (e) {
            console.error('error getSiblingDiscount: ', e);
            return null;
        }
    }

    async paymentRequest(athleteId: string, paymentMethod: string, amount: number, categoryId: string, invoiceParents: string[], successUrl: string, errorUrl: string) {
        let response: any;

        try {
            response = await this.http.post(environment.apiUrl + '/athletePaymentRequests', {
                athleteId,
                paymentMethod,
                amount,
                categoryId,
                invoiceParents,
                successUrl,
                errorUrl
            }, {headers: this.getRequestHeaders()}).toPromise();

            return response;

        } catch (e) {
            console.error('error paymentRequest: ', e)
        }
    }

    async verifyPaymentAppPago(params: any) {
        let response: any;
        try {
            response = await this.http.get(
                `${environment.apiUrl}/appPago/verifyPayment/`,
                {
                    params,
                    headers: this.getRequestHeaders(),
                }
            ).toPromise();
            return response;

        } catch (e) {
            console.error('error verifyPayment: ', e)
        }
    }

    async getAllPayments(params: any = {}) {
        let response: any;

        try {
            response = await this.http.get(environment.apiUrl + '/allAthletePaymentRequests', {
                params: params,
                headers: this.getRequestHeaders()
            }).toPromise();
            console.log("response getAllPayments: ", response)
            return response;

        } catch (e) {
            console.error('error getAllPayments: ', e)
            return {success: false, error: e};
        }
    }

    async getRecurringPaymentDetails(paymentRequestId: string) {
        return this.http.get<any>(
            `${environment.apiUrl}/athletePaymentRequests/${encodeURIComponent(paymentRequestId)}/installments`,
            {headers: this.getRequestHeaders()}
        ).toPromise();
    }

    async replaceRecurringCard(paymentRequestId: string, installmentNumber: number) {
        return this.http.put<any>(
            `${environment.apiUrl}/athletePaymentRequests/${encodeURIComponent(paymentRequestId)}/installments/${installmentNumber}/replaceCard`,
            {},
            {headers: this.getRequestHeaders()}
        ).toPromise();
    }

    async updatePaymentAmount(paymentId: string, amount: number, status?: string) {
        try {
            const body: any = {
                amount: amount
            };
            if (status) body.status = status;
            const response: any = await this.http.put(
                `${environment.apiUrl}/athletePaymentRequests/updateAmount/${paymentId}`,
                body,
                {headers: this.getRequestHeaders()}
            ).toPromise();
            return response;
        } catch (e) {
            console.error('error updatePaymentAmount:', e);
            return {success: false, error: e};
        }
    }

    async getStripeEmbeddedCheckoutClientSecret() {
        let response: any;

        try {
            response = await this.http.get(
                environment.apiUrl + '/stripe/embedded-checkout/client-secret',
                { headers: this.getRequestHeaders() }
            ).toPromise();

            return response;
        } catch (e) {
            console.error('error getStripeEmbeddedCheckoutClientSecret: ', e);
        }
    }

    async getServiceCheckoutClientSecret(serviceId: string, billingPeriod: 'monthly' | 'yearly', email?: string) {
        let response: any;

        try {
            response = await this.http.post(
                environment.apiUrl + '/services/checkout',
                {serviceId, billingPeriod, email: email?.trim() || undefined},
                {headers: this.getRequestHeaders()}
            ).toPromise();

            return response;
        } catch (e) {
            console.error('error getServiceCheckoutClientSecret: ', e);
        }
    }

    async completeCheckoutSession(sessionId: string, companyId?: string) {
        try {
            return await this.http.post(
                environment.apiUrl + '/services/checkout/complete',
                {sessionId, companyId: companyId?.trim() || undefined},
                {headers: this.getRequestHeaders()}
            ).toPromise();
        } catch (e: any) {
            console.error('error completeCheckoutSession: ', e);
            return e?.error ?? {success: false, error: 'Unable to complete checkout'};
        }
    }

    async getOldCompanyCheckoutClientSecret(
        companyId: string,
        billingPeriod: 'monthly' | 'yearly',
        serviceId: string,
        email?: string,
        options?: {replaceExisting?: boolean},
    ) {
        let response: any;
        try {
            response = await this.http.post(
                environment.apiUrl + '/services/checkout/old-company',
                {
                    companyId,
                    billingPeriod,
                    serviceId,
                    email: email?.trim() || undefined,
                    replaceExisting: options?.replaceExisting || undefined,
                },
                {headers: this.getRequestHeaders()}
            ).toPromise();
            return response;
        } catch (e: any) {
            console.error('error getCompanyCheckoutClientSecret: ', e);
            // Propagate backend error payload so callers can handle cases like:
            // { success: false, error: 'Company already has a service' }
            return e?.error ?? null;
        }
    }

    async getUpdatePaymentMethodClientSecret(companyId?: string) {
        let response: any;

        try {
            response = await this.http.post(
                environment.apiUrl + '/services/checkout/update-payment-method',
                companyId ? {companyId} : {},
                {headers: this.getRequestHeaders()}
            ).toPromise();

            return response;
        } catch (e) {
            console.error('error getUpdatePaymentMethodClientSecret: ', e);
        }
    }

    async retryCompanyPayment(companyId?: string): Promise<{ invoiceId: string; status: string; paid: boolean } | null> {
        try {
            const response: any = await this.http.post(
                environment.apiUrl + '/services/checkout/retry-payment',
                companyId ? { companyId } : {},
                { headers: this.getRequestHeaders() }
            ).toPromise();

            const payload = response?.data ?? response;
            if (!payload) return null;

            return {
                invoiceId: payload.invoiceId ?? '',
                status: payload.status ?? '',
                paid: !!payload.paid,
            };
        } catch (e) {
            console.error('error retryCompanyPayment: ', e);
            return null;
        }
    }

    async getStripeCardDetails(companyId: string) {
        let response: any;

        try {
            response = await this.http.get(
                environment.apiUrl + '/services/checkout/get-card-details?companyId=' + companyId,
                {headers: this.getRequestHeaders()}
                
            ).toPromise();

            return response;
        } catch (e) {
            console.error('error getStripeCardDetails: ', e);
        }
    }

    async getInvoice(companyId: string): Promise<{ success: boolean; data?: StripeInvoice }> {
        try {
            const response: any = await this.http.get(
                environment.apiUrl + '/services/checkout/get-invoice?companyId=' + companyId,
                { headers: this.getRequestHeaders() }
            ).toPromise();
            const invoice = response?.data?.invoice ?? response?.data;
            return {
                success: !!response?.success,
                data: invoice ?? undefined,
            };
        } catch (e) {
            console.error('error getInvoice: ', e);
            return { success: false };
        }
    }
}
